import { config } from "@config/config";
import { faker } from "@faker-js/faker";
import { ArrowPathIcon, CheckIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { FeatureFlagGate } from "@utils/FeatureFlags/FeatureFlagGate";
import { unwrapConnection } from "@utils/utils";
import ApexCharts from "apexcharts";
import { format } from "date-fns";
import { useCallback } from "react";

import { Button, Label, SlideOver } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportStatusChoices,
  ApiReportReportTypeChoices,
  ApiReportTargetAiProgressSummaryPredictionStatusChoices,
  GetReportByIdQuery,
  ReportGoalType,
  ReportTargetType,
  TargetTimelineEstimationEnums,
  useReportTargetPanelSingleViewQuery,
  useStartTargetProgressSummaryWorkflowMutation,
  useUpdateTargetProgressSummaryMutation,
} from "@api/graphql/types-and-hooks";
import { DevOnlyWrapper } from "@components/DevOnlyWrapper/DevOnlyWrapper";
import { GOAL_FIELD_IDS } from "@components/GoalsSections/helper";
import { InlineEditFieldWithAI } from "@components/ProgrammingReview/InlineEditFieldWithAI";
import { getTargetFieldGenerationStatus } from "@components/ShortTermGoalPanel/helpers";
import { TargetProgressGraph } from "@components/TargetPanel/TargetProgressGraph";
import { TreatmentPlanForm, TreatmentPlanFormSkeleton } from "@components/TreatmentPlanFieldForm/TreatmentPlanForm";
import { useInlineTreatmentPlanEdit } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";
import { useTreatmentPlanTemplate } from "@components/TreatmentPlanFieldForm/useTreatmentPlanTemplate";
import { targetToFieldValues } from "@components/TreatmentPlanFieldForm/utils";
import { notifyError } from "@components/notifications/notifications";

interface TargetPanelSingleViewProps {
  title: string;
  isTargetPanelOpen: boolean;
  setTargetPanelOpen: (open: boolean) => void;
  shortTermGoal?: ReportGoalType | undefined;
  selectedTarget?: ReportTargetType | undefined;
  refetch: () => Promise<unknown>;
  reportQuery: UseQueryResult<GetReportByIdQuery, unknown>;
  readOnly?: boolean;
}

export const TargetPanelSingleView: React.FC<TargetPanelSingleViewProps> = ({
  isTargetPanelOpen,
  setTargetPanelOpen,
  title,
  shortTermGoal,
  selectedTarget,
  refetch,
  reportQuery,
  readOnly = false,
}) => {
  const queryClient = useQueryClient();

  const reportObject = reportQuery?.data?.getReports?.edges?.[0]?.node;
  const learnerId = reportObject?.learner?.id || null;

  const { targetFields } = useTreatmentPlanTemplate(reportObject?.id);

  const updateTargetProgressSummaryMutation = useUpdateTargetProgressSummaryMutation({});

  const isReassessment = reportObject?.reportType === ApiReportReportTypeChoices.Reassessment;
  const isReportCompleted = reportObject?.reportStatus === ApiReportReportStatusChoices.Completed;
  const targetId = selectedTarget?.id || "";

  const targetPanelQuery = useReportTargetPanelSingleViewQuery(
    {
      filter: {
        learnerId: learnerId || "",
        targetId: targetId || "",
        goalId: shortTermGoal?.id || "<missing-short-term-goal-id>",
        reportDomainId: shortTermGoal?.reportDomain?.id || "",
      },
    },
    {
      queryKey: ["getReportTargets", { reportGoalId: shortTermGoal?.id || "<missing-short-term-goal-id>" }, targetId],
      enabled: !!shortTermGoal?.id && !!targetId,
      refetchOnWindowFocus: false,
      refetchInterval: (query) => {
        const target = query.state.data?.getReportTargets?.edges?.[0]?.node;
        if (!target) return false;

        const isPending =
          target.aiProgressSummaryPredictionStatus ===
          ApiReportTargetAiProgressSummaryPredictionStatusChoices.GoalPredictionPending;

        return isPending ? config.REPORT_POLLING_INTERVAL : false;
      },
    },
  );

  const targetData = targetPanelQuery.data?.getReportTargets?.edges?.[0]?.node || undefined;
  const isMasteryCriteriaFieldInTemplate = targetFields.some(
    (field) => field.fieldIdentifier === GOAL_FIELD_IDS.MASTERY_CRITERIA,
  );
  const subTitle = isMasteryCriteriaFieldInTemplate ? (targetData?.masteryCriteria as string) : undefined;

  const invalidateTargetQuery = () => {
    if (targetId) {
      queryClient.invalidateQueries({
        queryKey: ["getReportTargets", { reportGoalId: shortTermGoal?.id || "<missing-short-term-goal-id>" }, targetId],
      });
    }
  };

  const handleUpdateTargetProgressSummary = async (
    targetId: string,
    isApproved: boolean,
    progressSummary: string | undefined,
  ) => {
    let chartImageUri = "";
    if (isApproved) {
      const chartId = `target_${targetId}_progress`;
      const chartInstance = ApexCharts.getChartByID(chartId);
      if (chartInstance) {
        try {
          const imageBlob = (await chartInstance.dataURI()) as { imgURI: string };
          chartImageUri = imageBlob.imgURI || "";
        } catch (error) {
          console.error("Failed to export target chart:", error);
        }
      }
    }

    await updateTargetProgressSummaryMutation.mutateAsync(
      {
        input: {
          targetId: targetId,
          isApproved: isApproved,
          progressSummary: progressSummary,
          chartImageUri: chartImageUri,
        },
      },
      {
        onSuccess: async (response) => {
          if (!response.updateTargetProgressSummary?.status) {
            notifyError("Failed to update target progress summary.");
            return;
          }
          await targetPanelQuery.refetch();
          await refetch();
        },
        onError: async () => {
          notifyError("Failed to update target progress summary.");
        },
      },
    );
  };

  const { saveTargetFields, saveTargetCustomField, isSaving } = useInlineTreatmentPlanEdit({
    goalId: shortTermGoal ? shortTermGoal.id : "<missing-short-term-goal-id>",
    reportId: reportObject?.id || "",
    domainId: shortTermGoal?.reportDomain?.id ?? null,
    learnerId: learnerId || "<missing-learner-id>",
    targetId: targetId || "",
    onSuccess: invalidateTargetQuery,
  });

  const startTargetProgressSummaryWorkflowMutation = useStartTargetProgressSummaryWorkflowMutation();
  const handleRegenerateSummary = () => {
    startTargetProgressSummaryWorkflowMutation.mutateAsync(
      {
        reportId: reportObject?.id || "<missing-report-id>",
        targetId: targetId || "<missing-target-id>",
      },
      {
        onSuccess(response) {
          if (response.startTargetProgressSummaryWorkflow?.status) {
            invalidateTargetQuery();
          } else {
            notifyError("Failed to regenerate target progress summary.");
          }
        },
        onError() {
          notifyError("Failed to regenerate target progress summary.");
        },
      },
    );
  };

  const renderDetailsSection = () => (
    <>
      <DevOnlyWrapper>
        <div className="my-4 flex items-center">
          <Button
            appearance="link"
            className="mr-2"
            text="Fill Fields (dev only)"
            onClick={() => {
              saveTargetFields({
                targetName: `${faker.word.verb()} ${faker.word.noun()}`,
                targetDescription: faker.word.words({ count: 20 }),
                masteryCriteria: faker.word.words({ count: 20 }),
                timelineEstimationValue: 8,
                timelineEstimationType: TargetTimelineEstimationEnums.Weeks,
                baselineData: "",
                completionDate: format(faker.date.future({ years: 1 }), "yyyy-MM-dd"),
              });
            }}
          />
          <Button
            appearance="link"
            text="Reset"
            onClick={() => {
              saveTargetFields({
                targetDescription: "",
                masteryCriteria: "",
                timelineEstimationValue: null,
                timelineEstimationType: null,
                baselineData: "",
                completionDate: null,
              });
            }}
          />
        </div>
      </DevOnlyWrapper>
      <div className="mt-6 flex flex-col gap-y-2">
        {targetPanelQuery.isLoading && <TreatmentPlanFormSkeleton count={Math.max(targetFields?.length || 0, 3)} />}
        {!targetPanelQuery.isLoading && (
          <TreatmentPlanForm
            templateFields={targetFields}
            nativeFieldValues={targetData ? targetToFieldValues(targetData) : {}}
            onNativeFieldSave={saveTargetFields}
            customFields={unwrapConnection(targetData?.customFields) ?? []}
            onCustomFieldSave={saveTargetCustomField}
            onImageUploaded={invalidateTargetQuery}
            isReportCompleted={isReportCompleted}
            isReadOnly={readOnly}
            domain={shortTermGoal?.reportDomain ?? null}
          />
        )}
      </div>
    </>
  );

  const { progressSummaryGenerating, progressSummaryFailed } = getTargetFieldGenerationStatus(
    targetData?.aiProgressSummaryPredictionStatus || null,
  );

  const renderProgressSection = () => (
    <FeatureFlagGate flagKey="reassessment-builder">
      {isReassessment && shortTermGoal && targetData ? (
        <div>
          <TargetProgressGraph
            reportGoalId={shortTermGoal.id}
            target={targetData}
            signedChartImageUrl={targetData?.signedChartImageUrl || ""}
          />
          <InlineEditFieldWithAI
            value={targetData?.progressSummary || ""}
            fieldKey="progressSummary"
            onSave={saveTargetFields}
            title="Progress Summary"
            isMultiline
            isReportCompleted={isReportCompleted}
            isGenerating={progressSummaryGenerating}
            isGenerated={!!targetData?.progressSummary}
            isError={progressSummaryFailed}
            errorMessage="AI progress summary generation failed"
            onRegenerate={handleRegenerateSummary}
            subTitle="How this goal has progressed over the last authorization period"
            canRegenerate={!targetData?.summaryReviewed && !readOnly}
          />
        </div>
      ) : null}
    </FeatureFlagGate>
  );

  const renderFooter = () => {
    return (
      <div className="flex justify-between items-center gap-2 shrink-0 border-t py-4 shadow-md px-6 bg-white border-limestone-100">
        <Label size="extra-small" colorType="secondary">
          {isSaving || targetPanelQuery.isLoading ? (
            <div className="flex flex-row items-center gap-x-2">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />{" "}
              {targetPanelQuery.isLoading ? "Loading..." : "Saving..."}
            </div>
          ) : targetData?.lastUpdatedDate || targetData?.createdDate ? (
            `Last saved ${format(new Date(targetData.lastUpdatedDate ?? targetData.createdDate), "h:mmaaa, M/d/yyyy")}`
          ) : null}
        </Label>
        {/* Approve/Revoke buttons for PROGRESS SUMMARY (like STG) */}
        {isReassessment && shortTermGoal?.goal?.id && targetData && (
          <div className="flex justify-end">
            {!readOnly && (
              <>
                {targetData?.summaryReviewed ? (
                  <Button
                    onClick={() =>
                      handleUpdateTargetProgressSummary(
                        targetData?.id || "",
                        false, // Revoke approval
                        targetData?.progressSummary || undefined,
                      )
                    }
                    text={
                      <div className="flex items-center gap-2">
                        {updateTargetProgressSummaryMutation.isPending || targetPanelQuery.isLoading ? (
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <XCircleIcon className="size-4" />
                        )}
                        Revoke
                      </div>
                    }
                    appearance="secondary"
                    className="transition-all duration-500 ease-in-out"
                  />
                ) : (
                  <Button
                    onClick={() =>
                      handleUpdateTargetProgressSummary(
                        targetData?.id || "",
                        true, // Approve
                        targetData?.progressSummary || undefined,
                      )
                    }
                    text={
                      <div className="flex items-center gap-2">
                        {updateTargetProgressSummaryMutation.isPending || targetPanelQuery.isLoading ? (
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckIcon className="size-4" />
                        )}
                        Approve
                      </div>
                    }
                    appearance="primary"
                    className="transition-all duration-500 ease-in-out"
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleClosePanel = useCallback(() => {
    setTargetPanelOpen(false);
    queryClient.removeQueries({
      queryKey: ["getReportTargets"],
      exact: false, // remove all target panel queries
    });
  }, [setTargetPanelOpen, queryClient]);

  const mainContent = () => {
    return (
      <div className="flex flex-col gap-y-2 pb-4">
        {isReassessment && targetData && shortTermGoal?.goal?.id && renderProgressSection()}
        {renderDetailsSection()}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <SlideOver
        title={title}
        subTitle={subTitle}
        open={isTargetPanelOpen}
        size={isReassessment ? "2xl" : "md"}
        setOpen={() => {
          if (isSaving) return;
          handleClosePanel();
        }}
        contentClassName="p-0! flex! flex-col! h-full! overflow-hidden!"
      >
        <div className="flex flex-col h-full min-h-0">
          <div className="flex-1 overflow-y-auto py-4 px-6">{mainContent()}</div>
          {renderFooter()}
        </div>
      </SlideOver>
    </div>
  );
};
